// Shared height model for the ART Objectives layout: baseline-px constants that
// must mirror TeamObjectivesColumn.vue, plus the pure helper that turns a team's
// objective counts into a rendered-height estimate. Consumed by
// computeNoWrapLayout to choose the global scale and pack columns.

export const MAX_CELL_HEIGHT = 96;
// Floor for the uniform scale. Cells render very small at the floor, but fitting
// beats overflowing.
export const MIN_CELL_HEIGHT = 24;

// At the smallest scale, the team header grows by this many pixels, making it easier
// for users to identify which objectives belong to which team.
const TEAM_HEADER_MIN_SCALE_PIXEL_BOOST = 3;

// Baseline-px — must mirror TeamObjectivesColumn.vue and team-slot padding.
const SLOT_PAD = 40; // team-slot padding 20 top + 20 bottom
const TEAM_HEADER_FONT = 16; // .team-name font-size (board.len(16px))
const TEAM_HEADER_TEXT_H = TEAM_HEADER_FONT * 1.4; // 22.4 — team-name line box
const TEAM_HEADER_PAD = 24; // .team-header padding: board.len(12px) top + bottom
const SECTION_HEADER_H = 15.6; // 13px × 1.2
const MIN_SCALE = MIN_CELL_HEIGHT / MAX_CELL_HEIGHT;
const GAP_AFTER_TEAM = 16;
const GAP_HEADER_TO_CELL = 4; // committedHeader → committedCell, uncommittedHeader → uncommittedCell
const GAP_CELL_TO_CELL = 0; // committedCell → committedCell, uncommittedCell → uncommittedCell
const GAP_CELL_TO_NEXT_SECTION = 10; // committedCell → uncommittedHeader
const GAP_BETWEEN_SECTIONS = 16; // committedHeader → uncommittedHeader (empty committed section)

export interface TeamCounts {
  committed: number;
  uncommitted: number;
}

type Child =
  | "teamHeader"
  | "committedHeader"
  | "uncommittedHeader"
  | "committedCell"
  | "uncommittedCell";

/**
 * Rendered height (baseline px) of a team's whole column block at the given cell
 * height. Mirrors TeamObjectivesColumn.vue: a team with no objectives renders as
 * its header alone; otherwise team header + committed section + uncommitted
 * section, with chrome and gaps scaled by cellH/MAX_CELL_HEIGHT (the same
 * `--art-scale` cascade the CSS uses).
 */
export function teamHeight(team: TeamCounts, cellH: number): number {
  const scale = cellH / MAX_CELL_HEIGHT;

  // Empty team renders as the team-header alone ("No objectives yet" inline).
  if (team.committed === 0 && team.uncommitted === 0) {
    return SLOT_PAD * scale + teamHeaderHeight(scale);
  }

  const children: Child[] = ["teamHeader", "committedHeader"];
  for (let i = 0; i < team.committed; i++) children.push("committedCell");
  children.push("uncommittedHeader");
  for (let i = 0; i < team.uncommitted; i++) children.push("uncommittedCell");

  let height = SLOT_PAD * scale;
  for (let i = 0; i < children.length; i++) {
    const c = children[i];
    if (c === "teamHeader") height += teamHeaderHeight(scale);
    else if (c === "committedHeader" || c === "uncommittedHeader")
      height += SECTION_HEADER_H * scale;
    else height += cellH;
    if (i > 0) height += gapBetween(children[i - 1], c) * scale;
  }
  return height;
}

/**
 * Adjusted scale of the team name text, allowing for it to be more visible at smaller board scales.
 * At full scale, the return is the same as `scale`.
 * At the smallest scale, the team header will be PIXEL_BOOST pixels larger.
 * (in between, the value is interpolated linearly).
 */
export function teamHeaderScale(scale: number): number {
  const t = (1 - scale) / (1 - MIN_SCALE); // 0 at full scale, 1 at the floor
  return scale + (TEAM_HEADER_MIN_SCALE_PIXEL_BOOST / TEAM_HEADER_FONT) * t;
}

// Header box height: boosted text (teamHeaderScale) + uniformly-scaled padding.
function teamHeaderHeight(scale: number): number {
  return TEAM_HEADER_TEXT_H * teamHeaderScale(scale) + TEAM_HEADER_PAD * scale;
}

/** Baseline-px gap between two adjacent rendered rows within a team block. */
function gapBetween(prev: Child, curr: Child): number {
  if (prev === "teamHeader") return GAP_AFTER_TEAM;
  if (
    (prev === "committedHeader" && curr === "committedCell") ||
    (prev === "uncommittedHeader" && curr === "uncommittedCell")
  ) {
    return GAP_HEADER_TO_CELL;
  }
  if (
    (prev === "committedCell" && curr === "committedCell") ||
    (prev === "uncommittedCell" && curr === "uncommittedCell")
  ) {
    return GAP_CELL_TO_CELL;
  }
  if (prev === "committedCell" && curr === "uncommittedHeader") return GAP_CELL_TO_NEXT_SECTION;
  return GAP_BETWEEN_SECTIONS;
}
